<script setup lang="ts">
import { computed } from "vue";
import SpeedIcon from "~icons/ic/baseline-speed";
import MikrotikIcon from "~icons/simple-icons/mikrotik";
import { defaultResetFn } from "../../common/defaults.common";
import type { MikrotikParam } from "../../common/types.common";
import { speedOptions } from "../../data/options";
import { defineActions, defineState } from "../common";
import { Buttons } from "../icons";
import { FormInputCheckbox, FormInputNumeric, FormInputNumericUnit, FormInputText, FormInputToggle } from "../input";
import { FormMessage, FormPanel, FormTool } from "../internal";
import { type BaseProps, defaultProps } from "../props";

type ModelType = MikrotikParam;
interface PropsType extends BaseProps<ModelType> {
    toggleable?: boolean;
    placeholder?: {
        [K in keyof ModelType]?: string | undefined;
    };
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
    value: undefined,
});

const model = defineModel<ModelType>({
    required: true,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);

const collapsed = computed(() => {
    if (props.toggleable) {
        return !model.value.isActive;
    } else {
        return false;
    }
});

// watch(
//     () => props.toggleable,
//     () => (model.value.isActive = props.toggleable === false),
//     { immediate: true },
// );

const showScramble = computed(
    () => import.meta.env.MODE !== "production" && state.randomizable && model.value.isActive,
);
</script>

<template>
    <FormPanel :collapsed="collapsed" :param="props">
        <template #header>
            <span class="inline-flex flex-row items-center gap-3">
                <FormInputCheckbox
                    v-if="props.toggleable"
                    v-model="model.isActive"
                    class="ml-1"
                    :disabled="!state.editMode"
                    embedMode="inline"
                />
                <MikrotikIcon v-else class="ml-1" />
                <span class="font-semibold text-gray-600 inline-flex flex-row gap-2 items-center">
                    {{ props.label ?? "Pengaturan MikroTik" }}
                    <Buttons.BtnRandom v-if="showScramble" @click="actions.onScramble" />
                    <Buttons.BtnReset v-if="showScramble && state.clearable" @click="actions.onReset" />
                </span>
            </span>
        </template>

        <template #icons>
            <span class="inline-flex flex-row">
                <FormTool :actions="actions" :param="props" :showUpdate="true" :state="state" />
            </span>
        </template>

        <div v-if="state.hasError" class="p-4">
            <FormMessage :state="state" />
        </div>

        <div class="flex flex-col gap-6">
            <FormInputText
                v-model="model.mikrotikProfile"
                :attachReset="defaultResetFn"
                embedMode="embed"
                label="MikroTik Profile"
                :mode="state.nestMode"
                :placeholder="props.placeholder?.mikrotikProfile ?? 'default'"
            />

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 overflow-clip">
                <FormInputNumericUnit
                    v-model="model.downloadSpeed"
                    :attachReset="defaultResetFn"
                    embedMode="embed"
                    label="Kecepatan Unduh"
                    :mode="state.nestMode"
                    :placeholder="props.placeholder?.downloadSpeed ?? '∞'"
                    :units="speedOptions"
                    zeroAsInfinite
                />
                <FormInputNumericUnit
                    v-model="model.uploadSpeed"
                    :attachReset="defaultResetFn"
                    embedMode="embed"
                    label="Kecepatan Unggah"
                    :mode="state.nestMode"
                    :placeholder="props.placeholder?.uploadSpeed ?? '∞'"
                    :units="speedOptions"
                    zeroAsInfinite
                />
            </div>

            <FormInputToggle v-model="model.useBurst" label="MikroTik Speed Burst" :mode="state.nestMode">
                <template #icon>
                    <SpeedIcon />
                </template>
            </FormInputToggle>

            <Transition name="rns-fade">
                <div v-show="model.useBurst" class="grid grid-cols-1 md:grid-cols-2 gap-6 overflow-clip">
                    <FormInputNumericUnit
                        v-model="model.downloadBurstLimit"
                        :attachReset="defaultResetFn"
                        :disabled="!model.useBurst"
                        embedMode="embed"
                        label="Download Burst Limit"
                        :mode="state.nestMode"
                        :placeholder="props.placeholder?.downloadBurstLimit ?? '∞'"
                        :units="speedOptions"
                        zeroAsInfinite
                    />
                    <FormInputNumericUnit
                        v-model="model.uploadBurstLimit"
                        :attachReset="defaultResetFn"
                        :disabled="!model.useBurst"
                        embedMode="embed"
                        label="Upload Burst Limit"
                        :mode="state.nestMode"
                        :placeholder="props.placeholder?.uploadBurstLimit ?? '∞'"
                        :units="speedOptions"
                        zeroAsInfinite
                    />
                    <FormInputNumericUnit
                        v-model="model.downloadBurstThreshold"
                        :attachReset="defaultResetFn"
                        :disabled="!model.useBurst"
                        embedMode="embed"
                        label="Download Burst Threshold"
                        :mode="state.nestMode"
                        :placeholder="props.placeholder?.downloadBurstThreshold ?? '∞'"
                        :units="speedOptions"
                        zeroAsInfinite
                    />
                    <FormInputNumericUnit
                        v-model="model.uploadBurstThreshold"
                        :attachReset="defaultResetFn"
                        :disabled="!model.useBurst"
                        embedMode="embed"
                        label="Upload Burst Threshold"
                        :mode="state.nestMode"
                        :placeholder="props.placeholder?.uploadBurstThreshold ?? '∞'"
                        :units="speedOptions"
                        zeroAsInfinite
                    />
                    <FormInputNumeric
                        v-model="model.downloadBurstTime"
                        :attachReset="defaultResetFn"
                        :disabled="!model.useBurst"
                        embedMode="embed"
                        label="Download Burst Time"
                        :mode="state.nestMode"
                        :placeholder="props.placeholder?.downloadBurstTime ?? '∞'"
                        unit="seconds"
                    />
                    <FormInputNumeric
                        v-model="model.uploadBurstTime"
                        :attachReset="defaultResetFn"
                        :disabled="!model.useBurst"
                        embedMode="embed"
                        label="Upload Burst Time"
                        :mode="state.nestMode"
                        :placeholder="props.placeholder?.uploadBurstTime ?? '∞'"
                        unit="seconds"
                    />
                    <FormInputNumeric
                        v-model="model.priority"
                        :attachReset="defaultResetFn"
                        class="col-span-full"
                        :disabled="!model.useBurst"
                        embedMode="embed"
                        label="Priority"
                        :max="8"
                        :min="1"
                        :mode="state.nestMode"
                        :placeholder="props.placeholder?.priority ?? '8'"
                    />
                    <FormInputNumericUnit
                        v-model="model.downloadLimitAt"
                        :attachReset="defaultResetFn"
                        :disabled="!model.useBurst"
                        embedMode="embed"
                        label="Download Limit At"
                        :max="model.downloadSpeed"
                        :mode="state.nestMode"
                        :placeholder="props.placeholder?.downloadLimitAt ?? '∞'"
                        :units="speedOptions"
                    />
                    <FormInputNumericUnit
                        v-model="model.uploadLimitAt"
                        :attachReset="defaultResetFn"
                        :disabled="!model.useBurst"
                        embedMode="embed"
                        label="Upload Limit At"
                        :max="model.uploadSpeed"
                        :mode="state.nestMode"
                        :placeholder="props.placeholder?.uploadLimitAt ?? '∞'"
                        :units="speedOptions"
                    />
                </div>
            </Transition>
        </div>
    </FormPanel>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-fade-enter-active,
.rns-fade-leave-active {
    transition: all 0.25s ease;
    max-height: 1920px;
}

.rns-fade-enter-from,
.rns-fade-leave-to {
    max-height: 0;
}
</style>
